import type { SelectItem } from "@fronterahealth/frontera-ui-components";

import { ApiLearnerFileFileKindChoices } from "@api/graphql/types-and-hooks";

// The interview respondent ("who was interviewed"). Kept in sync with the backend
// allowed list; `related_provider` is intentionally folded into `other` for now.
export type InterviewType = "parent" | "child" | "teacher" | "other";

const INTERVIEW_TYPES: InterviewType[] = ["parent", "child", "teacher", "other"];

// Respondents shown in the picker. Child/Teacher are hidden for now but kept in
// INTERVIEW_TYPES (still parse, re-enable by adding them back here).
export const SELECTABLE_INTERVIEW_TYPES: InterviewType[] = ["parent", "other"];

const INTERVIEW_TYPE_LABELS: Record<InterviewType, string> = {
  parent: "Parent",
  child: "Child",
  teacher: "Teacher",
  other: "Other",
};

export const DEFAULT_INTERVIEW_TYPE: InterviewType = "other";

// Collapse a respondent to one the picker actually shows, so a stored child/teacher value
// maps to a real option (Other) instead of leaving the dropdown blank or treating the
// fallback as a user change.
export const toSelectableInterviewType = (type: InterviewType): InterviewType =>
  SELECTABLE_INTERVIEW_TYPES.includes(type) ? type : DEFAULT_INTERVIEW_TYPE;

// Composite Document Type option ids encode both the file kind (INTERVIEW) and the
// respondent, so a single dropdown can capture both without a second picker. Format:
// "INTERVIEW::parent". The backend still stores file_kind + metadata.interview_type
// separately; this is a frontend-only presentation.
const SEPARATOR = "::";

export const makeInterviewOptionId = (type: InterviewType): string =>
  `${ApiLearnerFileFileKindChoices.Interview}${SEPARATOR}${type}`;

export const isInterviewOptionId = (id?: string | null): boolean =>
  !!id && id.startsWith(`${ApiLearnerFileFileKindChoices.Interview}${SEPARATOR}`);

export const parseInterviewType = (id?: string | null): InterviewType | null => {
  if (!isInterviewOptionId(id)) return null;
  const candidate = id!.split(SEPARATOR)[1] as InterviewType;
  return INTERVIEW_TYPES.includes(candidate) ? candidate : null;
};

// Read the interview respondent off a LearnerFile's metadata blob, defaulting to
// `other` when absent or unparseable.
export const interviewTypeFromMetadata = (metadata?: string | null): InterviewType => {
  if (!metadata) return DEFAULT_INTERVIEW_TYPE;
  try {
    const parsed = typeof metadata === "string" ? JSON.parse(metadata) : metadata;
    const value = parsed && typeof parsed === "object" ? parsed.interview_type : undefined;
    return INTERVIEW_TYPES.includes(value) ? (value as InterviewType) : DEFAULT_INTERVIEW_TYPE;
  } catch {
    return DEFAULT_INTERVIEW_TYPE;
  }
};

// Replace a single "Interview" document-type option with one composite option per
// respondent, preserving the original label (e.g. "Interview Recording - Parent").
export const expandInterviewOptions = (options: SelectItem[]): SelectItem[] =>
  options.flatMap((option) =>
    option.id === ApiLearnerFileFileKindChoices.Interview
      ? SELECTABLE_INTERVIEW_TYPES.map((type) => ({
          ...option,
          id: makeInterviewOptionId(type),
          primary: `${option.primary} - ${INTERVIEW_TYPE_LABELS[type]}`,
        }))
      : [option],
  );
